/**
 * ColumnConfirmCard — shows the column the backend flagged as the barcode
 * column, with its sample values + confidence score. Two branches:
 *
 *  - high / medium confidence: "Looks like column X. Right?" + yes/no
 *  - low confidence: pick-from-list with sample values for each column
 *
 */

import { useState } from 'react'
import { Check, ChevronDown } from 'lucide-react'
import type { UploadResult } from '../api'
import { AgentBubble } from './AgentBubble'

interface ColumnConfirmCardProps {
  upload: UploadResult
  busy: boolean
  onConfirm: (columnIndex: number) => void
}

export function ColumnConfirmCard({ upload, busy, onConfirm }: ColumnConfirmCardProps) {
  const detected =
    upload.detectedBarcodeColumn != null
      ? upload.columns.find((c) => c.index === upload.detectedBarcodeColumn)
      : undefined
  const highOrMedium =
    upload.detectedConfidence !== 'low' && detected !== undefined
  const [overriding, setOverriding] = useState(!highOrMedium)

  if (highOrMedium && detected && !overriding) {
    return (
      <div className="w-full max-w-xl space-y-4">
        <AgentBubble from="agent">
          I see <strong>{upload.rowCount.toLocaleString()}</strong> rows.
          Column <strong>"{detected.header || `#${detected.index + 1}`}"</strong>{' '}
          looks like the barcodes — {detected.sampleValues.slice(0, 3).join(', ')}
          . Right?
        </AgentBubble>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => onConfirm(detected.index)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-carnival-400 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-carnival-500 disabled:opacity-50"
          >
            <Check className="h-4 w-4" />
            Yes, that's it
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => setOverriding(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            No, actually it's…
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full max-w-xl space-y-4">
      <AgentBubble from="agent">
        {upload.detectedConfidence === 'low'
          ? "I'm not sure which column has the barcodes. Pick one:"
          : 'Which column has the barcodes?'}
      </AgentBubble>

      <div className="divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white shadow-sm dark:divide-slate-700 dark:border-slate-700 dark:bg-slate-800">
        {upload.columns.map((col) => (
          <button
            key={col.index}
            type="button"
            disabled={busy}
            onClick={() => onConfirm(col.index)}
            className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left text-sm transition hover:bg-carnival-50 disabled:opacity-50 dark:hover:bg-carnival-400/10"
          >
            <div className="min-w-0 flex-1">
              <div className="font-medium text-slate-900 dark:text-slate-100">
                {col.header || `Column ${col.index + 1}`}
              </div>
              <div className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
                {col.sampleValues.slice(0, 3).join(' · ') || '—'}
              </div>
            </div>
            <div className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500">
              {Math.round(col.numericRatio * 100)}% numeric
            </div>
            <ChevronDown className="h-4 w-4 shrink-0 -rotate-90 text-slate-400" />
          </button>
        ))}
      </div>
    </div>
  )
}
